import { ReactNode } from "react";
import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchOutlined";

export interface FilterOption {
  label: string;
  value: string;
}

interface TableToolbarProps {
  /** Result count rendered emphasised ahead of the label. */
  count?: number;
  /** Describes what is being counted, e.g. "Fälle". */
  label?: string;
  /** Shows a search field when provided. */
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  /** Shows a single-select filter when provided. */
  filter?: {
    value: string;
    onChange: (value: string) => void;
    options: FilterOption[];
    allLabel?: string;
  };
  /** Trailing controls - export, view switches, primary action. */
  actions?: ReactNode;
}

/**
 * Header strip above a data table: result count on the left,
 * search / filter / actions on the right.
 */
export const TableToolbar = ({
  count,
  label,
  search,
  filter,
  actions,
}: TableToolbarProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      flexWrap: "wrap",
      px: 2,
      py: 1.5,
      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
    }}
  >
    {(count !== undefined || label) && (
      <Typography sx={{ fontSize: "0.9375rem", color: "text.secondary" }}>
        {count !== undefined && (
          <Box
            component="span"
            sx={{ fontWeight: 700, color: "text.primary", mr: 0.75 }}
          >
            {count}
          </Box>
        )}
        {label}
      </Typography>
    )}

    <Box sx={{ flexGrow: 1 }} />

    {search && (
      <TextField
        size="small"
        value={search.value}
        onChange={(event) => search.onChange(event.target.value)}
        placeholder={search.placeholder ?? "Suchen"}
        sx={{ width: { xs: "100%", sm: 220 } }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "1.125rem", color: "text.disabled" }} />
              </InputAdornment>
            ),
          },
        }}
      />
    )}

    {filter && (
      <TextField
        select
        size="small"
        value={filter.value}
        onChange={(event) => filter.onChange(event.target.value)}
        sx={{ minWidth: 150 }}
        // Without displayEmpty the "all" option renders as a blank field.
        slotProps={{ select: { displayEmpty: true } }}
      >
        <MenuItem value="">{filter.allLabel ?? "Alle"}</MenuItem>
        {filter.options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    )}

    {actions && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {actions}
      </Box>
    )}
  </Box>
);

export default TableToolbar;
